/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo, useState} from 'react';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {ExpandCollapseIconDirectionEnum, IconSize} from '@ion/dls/src/types/types';
import {MaterialFilledSearchIcon} from './MaterialFilledSearchIcon';
import {MaterialFilledUpIcon} from './MaterialFilledUpIcon';

type Props = {
  direction?: keyof typeof ExpandCollapseIconDirectionEnum;
  isActive?: boolean;
};

/**
 * @ExpandCollapseIcon
 *
 * Grey Icon for up or down navigation.
 *
 * ```typescript
 * import React from 'react';
 * import { ExpandCollapseIcon } from '@ion/dls/src/icons/';
 *
 * function MyComponent {
 *   return (
 *     <>
 *       <ExpandCollapseIcon direction="up" />
 *     </>
 *   );
 * }
 *
 * ```
 *
 */
export const ExpandCollapseIcon = ({direction = ExpandCollapseIconDirectionEnum.up, isActive}: Props) => {
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);

  const getColor = useCallback((isMouseDown, isMouseOver) => {
    let color = dlsColor('grey3');
    if (isMouseDown || isMouseOver || isActive) {
      color = dlsColor('grey1');
    }
    return color;
  }, [isMouseDown, isMouseOver, isActive]);

  const iconProps = useMemo(() => {
    return {
      size  : IconSize.large,
      color : getColor(isMouseDown, isMouseOver)
    };
  }, [isMouseDown, isMouseOver]);

  return (
    <span onMouseLeave={function() {
      setIsMouseOver(false);
    }} onMouseDown={function() {
      setIsMouseDown(true);
    }} onMouseUp={function() {
      setIsMouseDown(false);
    }} onMouseEnter={function() {
      setIsMouseOver(true);
    }}>
      {direction === ExpandCollapseIconDirectionEnum.up ? (
        <MaterialFilledUpIcon {...iconProps} />
      ) : (
        <MaterialFilledSearchIcon {...iconProps} />
      )}
    </span>
  );
};
